import {
  Card,
  CardBody,
  CardHeader,
  Icon,
  Table,
  useToggle,
} from '@met/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ConfirmationModal, PageHeader } from '../../components';
import { translationsActions } from '../../store';
import AddKeyModal from './components/AddKeyModal';
import TranslationRow from './components/TranslationRow';

const cols = {
  translationKey: {
    label: 'Key',
  },
  referenceValue: {
    label: 'Reference Value',
  },
  translation: {
    label: 'Translation',
  },
  actions: {
    label: 'Actions',
  },
};

const Translations = props => {
  const dispatch = useDispatch();
  const {
    translations,
    projectName,
    languageCode,
    namespaceName,
  } = useSelector(state => state.translations.data);
  const [keyModalHidden, toggleKeyModal] = useToggle(true);
  const { projectId, namespaceId, languageId } = props.match.params;
  const [deleteModalHidden, toggleDeleteModal] = useToggle(true);
  const [translationKeyToDelete, setTranslationKeyToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(
      translationsActions.getTranslations(projectId, languageId, namespaceId)
    ).then(() => {
      setIsLoading(false);
    });
  }, [dispatch, projectId, namespaceId, languageId]);

  useEffect(() => {
    if (!isLoading && window.location.hash) {
      let element = document.getElementById(window.location.hash);
      if (element) {
        setTimeout(() => {
          let y = element.getBoundingClientRect().top + window.scrollY;
          window.scroll({ top: y, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [isLoading]);

  const handleAddKeySave = useCallback(
    (key, value) => {
      return dispatch(
        translationsActions.upsertPrimaryTranslation(
          namespaceId,
          languageId,
          key,
          value
        )
      ).then(() =>
        dispatch(
          translationsActions.getTranslations(
            projectId,
            languageId,
            namespaceId
          )
        )
      );
    },
    [dispatch, namespaceId, languageId, projectId]
  );

  const handleTranslationUpdate = useCallback(
    (translationKeyId, value) => {
      return dispatch(
        translationsActions.upsertTranslation(
          namespaceId,
          languageId,
          translationKeyId,
          value
        )
      ).then(() => toast.success(`Translation updated to '${value}'`));
    },
    [dispatch, languageId, namespaceId]
  );

  const handleDeleteTranslationKey = translationKeyId => {
    setTranslationKeyToDelete(translationKeyId);
    toggleDeleteModal();
  };

  const deleteTranslation = useCallback(() => {
    return dispatch(
      translationsActions.deleteTranslationKey(translationKeyToDelete)
    )
      .then(() =>
        dispatch(
          translationsActions.getTranslations(
            projectId,
            languageId,
            namespaceId
          )
        )
      )
      .then(() => toggleDeleteModal());
  }, [
    dispatch,
    translationKeyToDelete,
    toggleDeleteModal,
    projectId,
    namespaceId,
    languageId,
  ]);

  const rows =
    translations &&
    translations.length > 0 &&
    translations.map((translation, index) => {
      return (
        <TranslationRow
          key={index}
          translation={translation}
          onUpdate={handleTranslationUpdate}
          onDelete={handleDeleteTranslationKey}
        />
      );
    });

  return (
    <div>
      <PageHeader
        title={
          <Link to={`/projects/${projectId}`}>
            {projectName} - {languageCode} - {namespaceName}
          </Link>
        }
        subtitle='Translations'
      />
      <Card>
        <CardHeader>
          <span>Translations</span>
          <div style={{ marginLeft: 'auto' }} id='table-actions' />
        </CardHeader>
        <CardBody>
          <Table
            striped
            borderless
            uniqueKeyId='translationId'
            manual
            columns={cols}
            data={translations}
            actionsToolbar={{
              targetElementId: 'table-actions',
              customActions: [
                {
                  label: 'Add Key',
                  icon: <Icon type='plus-circle' />,
                  onClick: toggleKeyModal,
                  disabled: languageCode !== 'en',
                },
              ],
            }}
          >
            {rows}
          </Table>
        </CardBody>
      </Card>
      <AddKeyModal
        hidden={keyModalHidden}
        toggle={toggleKeyModal}
        onSaveClick={handleAddKeySave}
      />
      <ConfirmationModal
        hidden={deleteModalHidden}
        toggle={toggleDeleteModal}
        onConfirm={deleteTranslation}
        body={
          'Are you sure you want to delete this translation? It will get removed across all languages for this project. This action cannot be undone.'
        }
      />
    </div>
  );
};

export default Translations;
